import React, { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { Banknote, FileCheck2, Handshake, NotebookPen, Info } from "lucide-react";
import { entriesAPI } from "@/api/entries";
import DateSelector from "./DateSelector";

type PaymentMethodValue = "cash_card" | "check" | "advance" | "open_account";

const METHODS: { value: PaymentMethodValue; label: string; icon: React.ElementType; hint: string }[] = [
  { value: "cash_card", label: "Nakit / Kredi Kartı", icon: Banknote, hint: "Maliyeti hemen artırır." },
  { value: "check", label: "Çek", icon: FileCheck2, hint: "Maliyet, çek ödendiğinde işlenir." },
  { value: "advance", label: "Anlaşma / Bağlantı", icon: Handshake, hint: "Kişiye peşin ödeme; sonraki masraflar bu avanstan düşülür." },
  { value: "open_account", label: "Açık Hesap", icon: NotebookPen, hint: "Sonra ödenecek; ödenene kadar maliyete eklenmez." },
];

const SUB_METHODS: { value: "cash_card" | "check"; label: string }[] = [
  { value: "cash_card", label: "Nakit / Kredi Kartı" },
  { value: "check", label: "Çek" },
];

/**
 * Payment-method step for the expense form. Renders the 4-way selector plus the
 * conditional fields (advance sub-method, check due date) and an inline advance
 * preview when an immediate expense is made for a person who holds prepaid credit.
 */
const PaymentMethodSection: React.FC = () => {
  const { control, setValue } = useFormContext();
  const paymentMethod = useWatch({ control, name: "paymentMethod" }) as PaymentMethodValue;
  const advanceSubMethod = useWatch({ control, name: "advanceSubMethod" }) as "cash_card" | "check" | undefined;
  const personId = useWatch({ control, name: "personId" }) as string | undefined;
  const projectId = useWatch({ control, name: "projectId" }) as string | undefined;

  const isImmediate = paymentMethod === "cash_card" || paymentMethod === "check";
  const needsDueDate =
    paymentMethod === "check" || (paymentMethod === "advance" && advanceSubMethod === "check");

  // Keep dependent fields consistent with the selected method.
  useEffect(() => {
    if (paymentMethod === "advance" || paymentMethod === "open_account") {
      // These require a person — Muhtelif is not allowed.
      setValue("isMiscellaneous", false);
    }
    if (paymentMethod !== "advance") {
      setValue("advanceSubMethod", undefined);
    }
    if (!needsDueDate) {
      setValue("dueDate", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod, advanceSubMethod]);

  // Advance preview for immediate expenses tied to a person.
  const [advanceCredit, setAdvanceCredit] = useState<number | null>(null);
  useEffect(() => {
    let active = true;
    if (isImmediate && personId && projectId) {
      entriesAPI
        .getPersonAdvanceBalance(personId, projectId)
        .then((map) => {
          if (active) setAdvanceCredit(map?.TRY ?? 0);
        })
        .catch(() => {
          if (active) setAdvanceCredit(null);
        });
    } else {
      setAdvanceCredit(null);
    }
    return () => {
      active = false;
    };
  }, [isImmediate, personId, projectId]);

  const activeHint = METHODS.find((m) => m.value === paymentMethod)?.hint;

  return (
    <div className="space-y-3">
      <FormField
        control={control}
        name="paymentMethod"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm text-muted-foreground mb-1">Ödeme Tipi</FormLabel>
            <FormControl>
              <div className="grid grid-cols-2 gap-2">
                {METHODS.map((m) => {
                  const Icon = m.icon;
                  const selected = field.value === m.value;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => field.onChange(m.value)}
                      className={cn(
                        "flex items-center gap-2 rounded-md border px-3 py-2.5 text-left text-[13px] transition-colors",
                        selected
                          ? "border-brand-600 bg-brand-50 text-brand-700"
                          : "border-input bg-background text-foreground hover:bg-muted/50"
                      )}
                    >
                      <Icon className={cn("h-4 w-4 shrink-0", selected ? "text-brand-600" : "text-muted-foreground")} />
                      <span className="leading-tight">{m.label}</span>
                    </button>
                  );
                })}
              </div>
            </FormControl>
            {activeHint && (
              <p className="mt-1 text-[12px] text-muted-foreground">{activeHint}</p>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Advance sub-method */}
      {paymentMethod === "advance" && (
        <FormField
          control={control}
          name="advanceSubMethod"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm text-muted-foreground mb-1">Ödeme Şekli</FormLabel>
              <FormControl>
                <div className="grid grid-cols-2 gap-2">
                  {SUB_METHODS.map((s) => {
                    const selected = field.value === s.value;
                    return (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => field.onChange(s.value)}
                        className={cn(
                          "rounded-md border px-3 py-2 text-[13px] transition-colors",
                          selected
                            ? "border-brand-600 bg-brand-50 text-brand-700"
                            : "border-input bg-background text-foreground hover:bg-muted/50"
                        )}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {/* Due date for checks */}
      {needsDueDate && (
        <DateSelector control={control} name="dueDate" label="Vade Tarihi" />
      )}

      {/* Person-required hint for advance / open account */}
      {(paymentMethod === "advance" || paymentMethod === "open_account") && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[12px] text-amber-700">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Bu ödeme tipi bir kişi ile ilişkilendirilmelidir. Aşağıdan kişi seçin.</span>
        </div>
      )}

      {/* Advance preview for immediate expenses */}
      {isImmediate && personId && advanceCredit !== null && advanceCredit > 0.005 && (
        <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-2.5 text-[12px] text-emerald-700">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Bu kişide kalan avans:{" "}
            <strong>
              {advanceCredit.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ₺
            </strong>
            . Bu masraf öncelikle avanstan düşülecek; maliyet yeniden artmayacak.
          </span>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodSection;
